import { ref, computed } from "vue";
import type { Frame, FrameState, HistoryState } from "../types";
import { CANVAS_CONFIG } from "../config";

export function useFrameState() {
  // 状态
  const frames = ref<Frame[]>([]);
  const currentFrameIndex = ref(0);

  // 历史记录状态
  const canUndo = ref(false);
  const canRedo = ref(false);

  // 计算属性
  const currentFrame = computed(() => frames.value[currentFrameIndex.value]);
  const hasFrames = computed(() => frames.value.length > 0);
  const frameCount = computed(() => frames.value.length);

  // 初始化帧数据
  const initializeFrames = async (imageUrls: string[]) => {
    frames.value = await Promise.all(
      imageUrls.map(async (url) => ({
        url,
        history: [],
        historyIndex: -1,
        isModified: false,
      }))
    );
    currentFrameIndex.value = 0;
  };

  // 帧操作
  const selectFrame = (index: number) => {
    if (index === currentFrameIndex.value) return false;

    switchToFrame(index);
    return true;
  };

  const switchToFrame = (index: number) => {
    if (index >= 0 && index < frames.value.length) {
      currentFrameIndex.value = index;
      updateHistoryButtons();
    }
  };

  const markFrameAsModified = () => {
    const frame = currentFrame.value;
    if (frame) {
      frame.isModified = true;
    }
  };

  const updateFrameData = (imageData: ImageData) => {
    const frame = currentFrame.value;
    if (!frame) return;

    // 将ImageData转换为DataURL
    const canvas = document.createElement("canvas");
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext("2d")!;
    ctx.putImageData(imageData, 0, 0);

    // 更新帧的URL
    frame.url = canvas.toDataURL("image/png");
  };

  // 历史记录操作
  const saveToHistory = (imageData: ImageData) => {
    const frame = currentFrame.value;
    if (!frame) return;

    // 截断当前位置之后的历史记录
    frame.history = frame.history.slice(0, frame.historyIndex + 1);
    frame.history.push(imageData);
    frame.historyIndex = frame.history.length - 1;

    // 限制历史记录数量
    if (frame.history.length > CANVAS_CONFIG.MAX_HISTORY_SIZE) {
      frame.history.shift();
      frame.historyIndex--;
    }

    updateHistoryButtons();
  };

  const undo = () => {
    const frame = currentFrame.value;
    if (!frame || frame.historyIndex <= 0) return null;

    frame.historyIndex--;
    updateHistoryButtons();
    markFrameAsModified();

    // 更新frame数据
    const imageData = frame.history[frame.historyIndex];
    updateFrameData(imageData);

    return imageData;
  };

  const redo = () => {
    const frame = currentFrame.value;
    if (!frame || frame.historyIndex >= frame.history.length - 1) return null;

    frame.historyIndex++;
    updateHistoryButtons();
    markFrameAsModified();

    // 更新frame数据
    const imageData = frame.history[frame.historyIndex];
    updateFrameData(imageData);

    return imageData;
  };

  const updateHistoryButtons = () => {
    const frame = currentFrame.value;
    if (!frame) {
      canUndo.value = false;
      canRedo.value = false;
      return;
    }

    canUndo.value = frame.historyIndex > 0;
    canRedo.value = frame.historyIndex < frame.history.length - 1;
  };

  // 导出操作
  const getFrameUrls = () => frames.value.map((frame) => frame.url);

  const getState = (): FrameState => ({
    frames: frames.value,
    currentFrameIndex: currentFrameIndex.value,
  });

  const getHistoryState = (): HistoryState => ({
    canUndo: canUndo.value,
    canRedo: canRedo.value,
  });

  return {
    // 状态
    frames,
    currentFrameIndex,
    canUndo,
    canRedo,

    // 计算属性
    currentFrame,
    hasFrames,
    frameCount,

    // 方法
    initializeFrames,
    selectFrame,
    switchToFrame,
    markFrameAsModified,
    updateFrameData,
    saveToHistory,
    undo,
    redo,
    updateHistoryButtons,
    getFrameUrls,
    getState,
    getHistoryState,
  };
}
